import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import Card from '@marketing/components/card';
import SectionHeader from '@marketing/components/section_header';
import UnderlinedText from '@marketing/components/underlined_text';

import BubbleTextIcon from './assets/bubble_text_icon.svg';
import QuestionMarksBg from './assets/question_marks_bg.svg';
import QuestionMarksBgMobile from './assets/question_marks_bg_mobile.svg';
import marketingStyles from '@marketing/style.module.scss';
import styles from './style.module.scss';

interface Props {
  entries: {
    answer: string | React.ReactNode;
    question: string;
  }[];
  footerNote?: string | React.ReactNode;
}

export default (props: Props) => {
  return (
    <div className="d-flex flex-column gap-l" id="faq" style={{ position: 'relative' }}>
      <img
        alt=""
        className="hidden-xs hidden-sm"
        loading="lazy"
        src={QuestionMarksBg}
        style={{
          left: '50%',
          position: 'absolute',
          transform: 'translateX(-50%)',
          width: '75%',
          zIndex: -2
        }}
      />
      <img
        alt=""
        className={cx(marketingStyles.fullContainerWidth, 'visible-xs visible-sm')}
        loading="lazy"
        src={QuestionMarksBgMobile}
        style={{
          left: 0,
          minWidth: '100vw',
          position: 'absolute',
          top: -50,
          width: '100vw',
          zIndex: -2
        }}
      />

      <SectionHeader
      subtitle={<>If you can't find the answer to your question below email us at <a className="text-body" href="mailto:help@magoosh.com">help@magoosh.com</a></>}
        supertitle="FAQ"
        supertitleIcon={BubbleTextIcon}
        title="Frequently asked questions"
      />

      <div className={styles.faqContainer}>
        <div
          style={{
            background: '#F7F7F7',
            border: '1px solid #DEDDDB',
            borderRadius: 22,
            boxShadow: 'inset 0 2px 3px rgba(0, 0, 0, 0.1)',
            padding: 10
          }}
        >
          <Card style={{ gap: 0, padding: 0 }}>
            {
              props.entries.map((entry, idx) => (
                <ExpandableQuestionAndAnswer
                  {...entry}
                  key={idx}
                  style={{ borderBottom: '1px solid #E1E1E1' }}
                />
              ))
            }
          </Card>
        </div>

        {
          props.footerNote && (
            <div className="small text-center u-margin-T-m">{props.footerNote}</div>
          )
        }
      </div>
    </div>
  );
};

const ExpandableQuestionAndAnswer: React.FC<{ answer: string | React.ReactNode; question: string; style: any; }> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const determineContentHeight = useRef(() => {
    if (ref.current) {
      setContentHeight(ref.current.clientHeight);
    }
  });

  useEffect(() => {
    determineContentHeight.current();

    window.addEventListener('resize', determineContentHeight.current);

    return () => {
      window.removeEventListener('resize', determineContentHeight.current);
    };
  }, [props.question]);

  return (
    <div className="d-flex flex-column" style={props.style}>
      <div
        className="align-items-center d-flex flex-1 gap-m"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer', padding: 20 }}
      >
        <div className="flex-1 text-inter" style={{ fontSize: 14 }}>
          <strong>{props.question}</strong>
        </div>

        <div style={{ transform: isExpanded ? 'rotate(180deg)': '' }}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.75 1L4.0429 4.2929C4.4334 4.6834 5.0666 4.6834 5.4571 4.2929L8.75 1" stroke="#B2B2B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      <div
        className="text-inter"
        style={{
          color: '#5C5C5C',
          height: isExpanded ? contentHeight : 0,
          overflow: 'hidden',
          paddingTop: 0,
          transition: 'height 200ms'
        }}
      >
        <div ref={ref} style={{ fontSize: 14, padding: 20, paddingTop: 0 }}>
          {props.answer}
        </div>
      </div>
    </div>
  );
};
